/**
 * DocsPage — the docs section.
 *
 * 4 routes:
 *   /docs             → index with two tiles (FVW + Pact)
 *   /docs/fvw         → list of FVW v8 sections
 *   /docs/fvw/:section → render one FVW section as Markdown
 *   /docs/pact        → list of Pact docs
 *   /docs/pact/:doc   → render one Pact doc as Markdown
 *
 * Per the gallery viewer's FVW v8 app shape, this is the docs section
 * that the user requested. It renders the canonical FVW v8 doctrine
 * and this app's own App-Pact.
 */

import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { marked } from 'marked';
import {
  fetchFvwSection,
  fetchPactDoc,
  listFvwSections,
  listPactDocs,
  type DocResult,
} from '../data/docs-loader';
import './DocsPage.css';

export function DocsPage() {
  const location = useLocation();
  const path = location.pathname;

  // Route detection (could also use nested routes, but this keeps it simple)
  if (path === '/docs') return <DocsIndex />;
  if (path === '/docs/fvw') return <FvwIndex />;
  if (path.startsWith('/docs/fvw/')) {
    const section = decodeURIComponent(path.replace('/docs/fvw/', ''));
    return <FvwSectionView section={section} />;
  }
  if (path === '/docs/pact') return <PactIndex />;
  if (path.startsWith('/docs/pact/')) {
    const doc = decodeURIComponent(path.replace('/docs/pact/', ''));
    return <PactDocView doc={doc} />;
  }
  return null;
}

function DocsIndex() {
  return (
    <div className="docs-page">
      <header className="docs-page__header">
        <h1 className="docs-page__title">Docs</h1>
        <p className="docs-page__tagline">
          The constitution and the rules. Every Fv Module Gallery module must obey these.
        </p>
      </header>

      <div className="docs-page__tiles">
        <Link to="/docs/fvw" className="docs-page__tile docs-page__tile--fvw">
          <h2>FVW v8 doctrine</h2>
          <p>The FreshVibe Way v8. The constitutional document for the Fv ecosystem. Read this first.</p>
          <span className="docs-page__tile-meta">35 sections · live from gallery</span>
        </Link>

        <Link to="/docs/pact" className="docs-page__tile docs-page__tile--pact">
          <h2>This app's pact</h2>
          <p>The App-Pact for the Fv Module Gallery viewer. Invariants, anti-drift rules, and the 6 rule categories.</p>
          <span className="docs-page__tile-meta">3 invariants · 7 anti-drift rules · 6 categories</span>
        </Link>
      </div>
    </div>
  );
}

function FvwIndex() {
  const [sections, setSections] = useState<string[] | null>(null);

  useEffect(() => {
    listFvwSections().then(setSections);
  }, []);

  return (
    <div className="docs-page">
      <Link to="/docs" className="docs-page__back">← Docs</Link>
      <header className="docs-page__header">
        <h1 className="docs-page__title">FVW v8 doctrine</h1>
        <p className="docs-page__tagline">
          The FreshVibe Way v8. The constitutional document for the Fv ecosystem.
        </p>
      </header>
      {!sections ? (
        <p>Loading…</p>
      ) : (
        <ul className="docs-page__list">
          {sections.map((s) => {
            const label = s.replace(/^\d+-/, '').replace(/-/g, ' ');
            const num = s.match(/^(\d+)-/)?.[1] ?? '?';
            return (
              <li key={s} className="docs-page__list-item">
                <Link to={`/docs/fvw/${encodeURIComponent(s)}`}>
                  <span className="docs-page__num">§{num}</span>
                  <span className="docs-page__label">{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FvwSectionView({ section }: { section: string }) {
  return <DocViewer section={section} kind="fvw" />;
}

function PactIndex() {
  const [docs, setDocs] = useState<string[] | null>(null);

  useEffect(() => {
    listPactDocs().then(setDocs);
  }, []);

  return (
    <div className="docs-page">
      <Link to="/docs" className="docs-page__back">← Docs</Link>
      <header className="docs-page__header">
        <h1 className="docs-page__title">This app's pact</h1>
        <p className="docs-page__tagline">
          The App-Pact for the Fv Module Gallery viewer.
        </p>
      </header>
      {!docs ? (
        <p>Loading…</p>
      ) : (
        <ul className="docs-page__list">
          {docs.map((d) => {
            const label = d.replace(/\.md$/, '').replace(/-/g, ' ');
            return (
              <li key={d} className="docs-page__list-item">
                <Link to={`/docs/pact/${encodeURIComponent(d)}`}>
                  <span className="docs-page__label">{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PactDocView({ doc }: { doc: string }) {
  return <DocViewer section={doc} kind="pact" />;
}

function DocViewer({ section, kind }: { section: string; kind: 'fvw' | 'pact' }) {
  const [result, setResult] = useState<DocResult | null>(null);

  useEffect(() => {
    const fetcher = kind === 'fvw' ? fetchFvwSection : fetchPactDoc;
    fetcher(section).then(setResult);
  }, [section, kind]);

  return (
    <div className="docs-page">
      <Link to={kind === 'fvw' ? '/docs/fvw' : '/docs/pact'} className="docs-page__back">
        ← {kind === 'fvw' ? 'FVW sections' : 'Pact docs'}
      </Link>

      {!result ? (
        <p>Loading…</p>
      ) : (
        <>
          <div className="docs-page__source">
            Source: <code>{result.source}</code> · path: <code>{result.path}</code>
          </div>
          <article
            className="docs-page__content"
            dangerouslySetInnerHTML={{
              __html: marked.parse(result.content, { async: false }) as string,
            }}
          />
        </>
      )}
    </div>
  );
}
